import { Suspense, lazy, useCallback, useEffect, useState } from 'react';
import { Box, Flex, Spinner } from '@chakra-ui/react';
import { DocumentIcon } from '@heroicons/react/24/outline';
import { logger } from '@/utils/logger';

// pdf.js is a large dependency and most screens never open a certificate, so the
// renderer is code-split behind this lazy boundary.
const PdfDocumentView = lazy(() => import('./PdfDocumentView'));

interface PDFPreviewProps {
  uri: string;
  width?: number;
  height?: number;
  /** `thumbnail` (default) fits page 1 inside the box; `full` fills and scrolls. */
  variant?: 'thumbnail' | 'full';
}

export default function PDFPreview({ uri, width = 80, height = 60, variant = 'thumbnail' }: PDFPreviewProps) {
  const [error, setError] = useState(false);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    setError(false);
    setReady(false);
  }, [uri]);

  const handleError = useCallback(
    (err: unknown) => {
      logger.error('[PDFPreview] failed to render PDF', { uri, err });
      setError(true);
    },
    [uri],
  );

  const handleReady = useCallback(() => setReady(true), []);

  const boxStyle = variant === 'full' ? undefined : { width, height };

  if (error || !uri) {
    return (
      <Flex
        alignItems="center"
        justifyContent="center"
        bg="#f5f5f5"
        borderRadius="lg"
        w={variant === 'full' ? 'full' : undefined}
        h={variant === 'full' ? 'full' : undefined}
        style={boxStyle}
      >
        <DocumentIcon width={width * 0.5} height={height * 0.5} color="#64748b" />
      </Flex>
    );
  }

  return (
    <Box
      position="relative"
      w="full"
      h="full"
      overflow="hidden"
      borderRadius="lg"
      bg="#f5f5f5"
      style={boxStyle}
    >
      {!ready && (
        <Flex position="absolute" inset="0" alignItems="center" justifyContent="center" pointerEvents="none">
          <Spinner size="sm" color="#64748b" />
        </Flex>
      )}
      <Suspense fallback={null}>
        <PdfDocumentView
          uri={uri}
          variant={variant}
          width={width}
          height={height}
          onError={handleError}
          onReady={handleReady}
        />
      </Suspense>
    </Box>
  );
}
